import { useCallback, useEffect, useState } from 'react';
import { api, type Asiento, type ResumenActor } from '../api';
import { EVENTOS_AUDITABLES, etiquetaDeEvento } from '../auditoria';
import { Tabla } from './Tabla';
import { fechaYHora } from '../fechas';

/**
 * Bitácora de accesos.
 *
 * Responde la pregunta que hace un cliente cuando pregunta por sus datos:
 * quién entró, desde dónde, qué miró y a quién se le negó algo. Solo la ve el
 * rol admin, y eso se aplica en el portal, no ocultando la pestaña.
 */


export function Auditoria({ desde, onError }: { desde: string; onError: (err: unknown) => void }) {
  const [asientos, setAsientos] = useState<Asiento[]>([]);
  const [actores, setActores] = useState<ResumenActor[]>([]);
  const [evento, setEvento] = useState('');
  const [actor, setActor] = useState('');
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [bitacora, resumen] = await Promise.all([
        api.auditoria(desde, evento || undefined, actor || undefined),
        api.auditoriaActores(desde),
      ]);
      setAsientos(bitacora.asientos);
      setActores(resumen.actores);
    } catch (err) {
      onError(err);
    } finally {
      setCargando(false);
    }
  }, [desde, evento, actor, onError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  return (
    <section className="auditoria">
      <div className="auditoria-filtros">
        <label>
          <span>Evento</span>
          <select value={evento} onChange={(e) => setEvento(e.target.value)}>
            <option value="">Todos</option>
            {EVENTOS_AUDITABLES.map((e) => (
              <option key={e} value={e}>
                {etiquetaDeEvento(e)}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Persona</span>
          <select value={actor} onChange={(e) => setActor(e.target.value)}>
            <option value="">Todas</option>
            {actores.map((a) => (
              <option key={a.actor_email} value={a.actor_email}>
                {a.actor_email} ({a.eventos})
              </option>
            ))}
          </select>
        </label>

        {(evento || actor) && (
          <button
            className="enlace"
            onClick={() => {
              setEvento('');
              setActor('');
            }}
          >
            limpiar filtros
          </button>
        )}

        <span className="conteo">
          {cargando ? 'Cargando…' : `${asientos.length} ${asientos.length === 1 ? 'asiento' : 'asientos'}`}
        </span>
      </div>

      {asientos.length === 0 && !cargando ? (
        <p className="vacio">Sin actividad registrada en esta ventana.</p>
      ) : (
        <Tabla>
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Quién</th>
              <th>Qué</th>
              <th>Detalle</th>
              <th>Desde</th>
            </tr>
          </thead>
          <tbody>
            {asientos.map((a, i) => (
              <tr key={`${a.ts}-${i}`} className={a.resultado !== 'ok' ? 'destacada' : ''}>
                <td className="momento">{fechaYHora(a.ts)}</td>
                <td>{a.actor_email || '—'}</td>
                <td>
                  <span className={`marca ${a.resultado}`}>{etiquetaDeEvento(a.evento)}</span>
                </td>
                <td className="detalle">{a.recurso || a.detalle || '—'}</td>
                <td>
                  <code>{a.ip}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}

      <p className="nota-retencion">
        La bitácora se conserva un año. Consultarla no genera asientos, para que abrirla no empuje
        hacia abajo lo que se vino a mirar.
      </p>
    </section>
  );
}
